import React, { useMemo, useState } from 'react'
import useSWRMutation from 'swr/mutation'
import dayjs from 'dayjs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/ui/use-toast'
import { postFetcher } from 'api/swr'

type SyncResult = {
  success: true
  key: string
  type: string
  ttlMs: number
  memberCount?: number
  source: string
  target: string
}

type ShortcutItem = { label: string; key: string }

type ShortcutEntity = { title: string; items: ShortcutItem[] }

/** 与 Worker MetricDateUtils.getDates 一致：metric 日上的 ISO 年-周 */
function metricIsoWeek(metricNow: dayjs.Dayjs): string {
  const d = metricNow.toDate()
  const utc = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
  const day = utc.getUTCDay() || 7
  utc.setUTCDate(utc.getUTCDate() + 4 - day)
  const isoYear = utc.getUTCFullYear()
  const yearStart = new Date(Date.UTC(isoYear, 0, 1))
  const isoWeek = Math.ceil(((+utc - +yearStart) / 86400000 + 1) / 7)
  return `${isoYear}-W${String(isoWeek).padStart(2, '0')}`
}

export default function SyncNekuRedisKeyTab() {
  const { toast } = useToast()
  const [key, setKey] = useState('')
  const [lastResult, setLastResult] = useState<SyncResult | null>(null)
  const { trigger, isMutating } = useSWRMutation(
    '/debug-tools/neku-redis/sync-key',
    postFetcher,
  )

  const shortcutEntities = useMemo((): ShortcutEntity[] => {
    // 与 Worker MetricDateUtils 一致：UTC − 8h 业务日
    const metricNow = dayjs().subtract(8, 'hour')
    const metricDate = metricNow.format('YYYY-MM-DD')
    const metricWeek = metricIsoWeek(metricNow)
    const metricMonth = metricNow.format('YYYY-MM')
    // 每日精选写的是已闭合业务日（昨日），与 Mask / worker 一致
    const closedDate = metricNow.subtract(1, 'day').format('YYYY-MM-DD')

    return [
      {
        title: '模板',
        items: [
          { label: 'New', key: 'frame:list:new:lang9:pid11' },
          {
            label: 'reCreate 总榜',
            key: 'frame:ranking:total:reCreate:lang9:pid11',
          },
          { label: '质量榜', key: 'frame:ranking:quality' },
          { label: 'Outfit', key: 'template:list:outfit' },
          { label: 'Outfit 隐藏', key: 'template:list:outfit:hidden' },
          { label: 'Spine', key: 'template:list:spine' },
          { label: 'Spine 隐藏', key: 'template:list:spine:hidden' },
          { label: 'Christmas', key: 'template:list:christmas' },
          { label: 'Christmas 隐藏', key: 'template:list:christmas:hidden' },
          {
            label: '日榜',
            key: `frame:ranking:daily:reCreate:${metricDate}:lang9:pid11`,
          },
          {
            label: '周榜',
            key: `frame:ranking:weekly:reCreate:${metricWeek}:lang9:pid11`,
          },
          {
            label: '月榜',
            key: `frame:ranking:monthly:reCreate:${metricMonth}:lang9:pid11`,
          },
        ],
      },
      {
        title: 'Avatar',
        items: [
          { label: 'New', key: 'avatar:list:new:pid11' },
          { label: 'New asia', key: 'avatar:list:new:asia' },
          { label: 'Like 总榜', key: 'avatar:ranking:total:like:pid11' },
          { label: 'Like asia', key: 'avatar:ranking:total:like:asia' },
          {
            label: '每日精选',
            key: `avatar:list:daily-featured:${closedDate}:lang9:pid11`,
          },
          {
            label: '日榜',
            key: `avatar:ranking:daily:reCreate:${metricDate}:pid11`,
          },
          {
            label: '日榜 simple',
            key: `avatar:ranking:daily:reCreate:simple:${metricDate}:pid11`,
          },
          {
            label: '日榜 asia',
            key: `avatar:ranking:daily:reCreate:asia:${metricDate}`,
          },
          {
            label: '周榜',
            key: `avatar:ranking:weekly:reCreate:${metricWeek}:pid11`,
          },
          {
            label: '周榜 simple',
            key: `avatar:ranking:weekly:reCreate:simple:${metricWeek}:pid11`,
          },
          {
            label: '周榜 asia',
            key: `avatar:ranking:weekly:reCreate:asia:${metricWeek}`,
          },
          {
            label: '月榜',
            key: `avatar:ranking:monthly:reCreate:${metricMonth}:pid11`,
          },
          {
            label: '月榜 simple',
            key: `avatar:ranking:monthly:reCreate:simple:${metricMonth}:pid11`,
          },
          {
            label: '月榜 asia',
            key: `avatar:ranking:monthly:reCreate:asia:${metricMonth}`,
          },
        ],
      },
    ]
  }, [])

  const onSync = async () => {
    const trimmed = key.trim()
    if (!trimmed) {
      toast({
        variant: 'destructive',
        title: '请输入 Redis key',
      })
      return
    }

    try {
      const result = (await trigger({ key: trimmed })) as SyncResult
      setLastResult(result)
      toast({
        title: '同步成功',
        description: `${result.type} · ${result.key}`,
      })
    } catch (err: unknown) {
      setLastResult(null)
      const message = err instanceof Error ? err.message : String(err)
      toast({
        variant: 'destructive',
        title: '同步失败',
        description: message,
      })
    }
  }

  return (
    <div className="max-w-4xl space-y-6 rounded-lg border p-6">
      <div className="space-y-2 text-sm text-muted-foreground leading-relaxed">
        <p>
          从生产 Neku 业务 Redis{' '}
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
            34.92.240.100:9376/0
          </code>{' '}
          复制单个 key 到测试环境{' '}
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
            45.79.21.219:9376/5
          </code>
          。测试环境已有则覆盖，没有则新建；生产不存在则报错。
        </p>
        <p>
          适用于 frame/avatar 榜单 ZSET、detail HASH 等单 key 调试。示例日期按
          UTC−8h 业务日生成；Avatar 亚洲区用 asia 过滤榜，模板仍按 lang。
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="neku-redis-key">Redis key</Label>
        <Input
          id="neku-redis-key"
          placeholder="例如 avatar:ranking:total:like:pid11"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              void onSync()
            }
          }}
        />
        <div className="grid gap-4 pt-2 md:grid-cols-2">
          {shortcutEntities.map((entity) => (
            <div
              key={entity.title}
              className="space-y-3 rounded-md border p-3"
            >
              <p className="text-sm font-medium">{entity.title}</p>
              <div className="flex flex-wrap gap-2">
                {entity.items.map((item) => {
                  const selected = key === item.key
                  return (
                    <button
                      key={item.key}
                      type="button"
                      title={item.key}
                      className={`max-w-full rounded-md border px-2 py-1 text-left hover:bg-muted ${
                        selected ? 'border-foreground bg-muted' : ''
                      }`}
                      onClick={() => setKey(item.key)}
                    >
                      <span className="block text-xs">{item.label}</span>
                      <span className="block max-w-[240px] truncate font-mono text-[10px] text-muted-foreground">
                        {item.key}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Button onClick={onSync} disabled={isMutating || !key.trim()}>
        {isMutating ? '同步中…' : '同步到测试环境'}
      </Button>

      {lastResult ? (
        <div className="rounded-md border bg-muted/40 p-4 text-sm space-y-1">
          <p>
            <span className="font-medium">key：</span>
            <code className="text-xs">{lastResult.key}</code>
          </p>
          <p>
            <span className="font-medium">type：</span>
            {lastResult.type}
          </p>
          {lastResult.memberCount != null ? (
            <p>
              <span className="font-medium">成员数：</span>
              {lastResult.memberCount}
            </p>
          ) : null}
          <p>
            <span className="font-medium">TTL：</span>
            {lastResult.ttlMs > 0
              ? `${Math.round(lastResult.ttlMs / 1000)}s`
              : '无过期'}
          </p>
          <p className="text-muted-foreground text-xs pt-1">
            {lastResult.source} → {lastResult.target}
          </p>
        </div>
      ) : null}
    </div>
  )
}
